import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Clock, UtensilsCrossed, ExternalLink } from 'lucide-react';
import { restaurantInfo, openingHours, kitchenHours, siteImages } from '../data/mock';

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isInView];
};

const GOOGLE_MAPS_LINK = "https://www.google.com/maps/place/La+Bodega/@53.587207,6.6576827,17z/data=!3m2!4b1!5s0x47c9e879744a6d7f:0xfbc7fb4ba49c13a9!4m6!3m5!1s0x47c9e95d288b3db9:0xaef0283f949a4bde!8m2!3d53.587207!4d6.6602576!16s%2Fg%2F11hdqvzkfh?entry=ttu&g_ep=EgoyMDI2MDQwOC4wIKXMDSoASAFQAw%3D%3D";

const Contact = () => {
  const [heroRef, heroInView] = useInView();
  const [contentRef, contentInView] = useInView();

  const contactItems = [
    {
      icon: MapPin,
      label: 'Adresse',
      value: restaurantInfo.address,
    },
    {
      icon: Phone,
      label: 'Telefon',
      value: restaurantInfo.phone,
      href: `tel:${restaurantInfo.phone}`,
    },
    {
      icon: Mail,
      label: 'E-Mail',
      value: restaurantInfo.email,
      href: `mailto:${restaurantInfo.email}`,
    },
  ];

  return (
    <div className="bg-[#0f0704]">
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 hero-parallax"
          style={{
            backgroundImage: `url('${siteImages.contactHero}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0f0704]" />
        <div className={`relative z-10 text-center px-6 ${heroInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <span className="text-amber-400 text-sm uppercase tracking-[0.35em] font-medium">
            Besuchen Sie uns
          </span>
          <h1 className="font-brand text-4xl md:text-6xl lg:text-7xl text-white mt-4">
            Kontakt
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section ref={contentRef} className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`${contentInView ? 'opacity-100' : 'opacity-0'}`}>
            {/* Top section — Contact info cards */}
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 ${contentInView ? 'animate-fade-in-up' : ''}`}>
              {contactItems.map((item) => {
                const Icon = item.icon;
                const content = (
                  <div className="group bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 text-center hover:bg-white/[0.06] hover:border-amber-400/20 transition-all duration-500">
                    <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-amber-400/10 flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-300">
                      <Icon size={26} className="text-amber-400" />
                    </div>
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-2">{item.label}</p>
                    <p className="text-white/90 text-sm font-medium">{item.value}</p>
                  </div>
                );
                if (item.href) {
                  return (
                    <a key={item.label} href={item.href} className="block">
                      {content}
                    </a>
                  );
                }
                return <div key={item.label}>{content}</div>;
              })}
            </div>

            {/* Opening Hours + Kitchen Hours */}
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16 ${contentInView ? 'animate-fade-in-up' : ''}`} style={{ animationDelay: '200ms' }}>
              {/* Opening Hours */}
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-amber-400/10 flex items-center justify-center">
                    <Clock size={22} className="text-amber-400" />
                  </div>
                  <h3 className="font-brand text-xl text-white">
                    Öffnungszeiten
                  </h3>
                </div>
                <div className="space-y-3">
                  {openingHours.map((item) => (
                    <div key={item.day} className="flex justify-between text-sm py-1 border-b border-white/[0.04] last:border-b-0">
                      <span className="text-white/50 font-medium">{item.day}</span>
                      <span className={item.hours === 'Ruhetag' ? 'text-red-400/80' : 'text-amber-400'}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kitchen Hours + Directions */}
              <div className="space-y-8">
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-400/10 flex items-center justify-center">
                      <UtensilsCrossed size={22} className="text-amber-400" />
                    </div>
                    <h3 className="font-brand text-xl text-white">
                      Warme Küche
                    </h3>
                  </div>
                  <p className="text-white/70 text-base leading-relaxed">
                    {kitchenHours}
                  </p>
                </div>

                <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-400/10 flex items-center justify-center">
                      <MapPin size={22} className="text-amber-400" />
                    </div>
                    <h3 className="font-brand text-xl text-white">
                      Anfahrt
                    </h3>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed mb-4">
                    Vom Bahnhof kommend in Richtung Meer, finden Sie uns kurz hinter der Kreuzung bei Akkermann auf der rechten Seite. Achten Sie auf das Reklameschild "Alfredos".
                  </p>
                  <a
                    href={GOOGLE_MAPS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-semibold tracking-wide transition-colors duration-200"
                  >
                    Auf Google Maps öffnen
                    <ExternalLink size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className={`rounded-2xl overflow-hidden border border-white/[0.06] ${contentInView ? 'animate-fade-in-up' : ''}`} style={{ animationDelay: '400ms' }}>
              <iframe
                title="La Bodega Borkum Standort"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2378.5!2d6.6576827!3d53.587207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c9e95d288b3db9%3A0xaef0283f949a4bde!2sLa%20Bodega!5e0!3m2!1sde!2sde!4v1713048000000"
                width="100%"
                height="400"
                style={{ border: 0, filter: 'grayscale(0.3) contrast(1.05)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
